/**
 * One works order, opened from the week it appears in: every operation, the machine it runs
 * on, and when.
 *
 * It sits BELOW the week panel rather than replacing it, because the useful reading is the
 * relationship between the two — this is the week, this is the order inside it, and here is
 * everywhere else that order goes. A works order crosses the estate (8009.02 visits a band
 * saw, the Matsuura, an EDM, four inspections and five finishing resources), so every
 * resource-shaped view on this page shows it as one anonymous slice of one week and none of
 * them can answer "where does this job go".
 *
 * Two absences are stated rather than left to be discovered: the subcontract legs that make
 * the operation numbers jump, and the sibling sub-works-orders that make this route less
 * than the whole job.
 */

import React from 'react'
import WorksOrderRoute from './WorksOrderRoute'
import { useWorksOrder } from '../../hooks/useLoading'
import type { ApiError } from '../../hooks/apiFetch'
import type { WorksOrderRouteView } from '../../types/worksOrder'
import { hrs } from './format'

const day = (iso: string | null): string =>
  iso === null ? '—' : new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })

function Head({ route, onClose }: { route: WorksOrderRouteView; onClose: () => void }): React.JSX.Element {
  const meta = [
    `${route.ops.length} operations`,
    `${route.machine_count} machines`,
    `${route.resource_groups.length} groups`,
    hrs(route.total_load_hours),
    `required ${day(route.required_date)}`,
    `runs ${day(route.first_start)} → ${day(route.last_finish)}`,
  ].join(' · ')
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 border-b px-4 py-3" style={{ borderColor: 'var(--lc-b09)' }}>
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-2.5">
          <span className="font-sans text-[13.5px] font-semibold" style={{ color: 'var(--lc-ink)' }}>
            Works order {route.wo_key}
          </span>
          <span className="font-mono text-[10.5px]" style={{ color: 'var(--lc-mut)' }}>
            {route.part_number ?? 'no part number'}
            {route.is_top_level ? ' · top level' : ` · in assembly ${route.assembly_key}`}
          </span>
        </div>
        <div className="font-mono text-[10.5px] leading-[1.4]" style={{ color: 'var(--lc-mut)' }}>{meta}</div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close the works order"
        className="rounded-md border px-2.5 py-1 font-sans text-[11px] font-semibold"
        style={{ borderColor: 'var(--lc-b12)', background: 'var(--lc-card)', color: 'var(--lc-ink3)' }}
      >
        Close ✕
      </button>
    </div>
  )
}

/** The two absences, plus the standing caveat. A route with gaps in its numbering reads as
 *  lost rows until something says what the gaps are. */
/** An assembly can hold seventeen sub-orders; naming all sixteen others buries the sentence
 *  they are in. The count is the finding, the first few are the lead. */
const siblingList = (siblings: string[]): string =>
  siblings.length <= 6
    ? siblings.join(', ')
    : `${siblings.slice(0, 6).join(', ')} and ${siblings.length - 6} more`

function Footnotes({ route }: { route: WorksOrderRouteView }): React.JSX.Element {
  const uncosted = route.ops.filter((o) => !o.is_cnc).length
  return (
    <div className="flex flex-col gap-1.5 border-t px-4 py-3" style={{ borderColor: 'var(--lc-b07)' }}>
      {route.siblings.length > 0 && (
        <p className="font-sans text-[10.5px] leading-[1.5]" style={{ color: 'var(--lc-mut)' }}>
          This is one of {route.siblings.length + 1} sub-works-orders in assembly{' '}
          <b>{route.assembly_key}</b> ({siblingList(route.siblings)}). Each carries its own route,
          so this is not the whole job in front of that delivery date.
        </p>
      )}
      {uncosted > 0 && (
        <p className="font-sans text-[10.5px] leading-[1.5]" style={{ color: 'var(--lc-mut)' }}>
          {uncosted} of {route.ops.length} operations run on resources with no agreed capacity
          basis (marked ·). They are in the route and in none of the load ratios on this page.
        </p>
      )}
      <p className="font-sans text-[10.5px] leading-[1.5]" style={{ color: 'var(--lc-faint)' }}>{route.note}.</p>
    </div>
  )
}

/** Loading, refused, or served-nothing — with a way out either way. A panel that can only
 *  be closed from its own header is a trap when the header is what failed to arrive. */
function Pending({ woKey, error, isLoading, onClose }: {
  woKey: string
  error: ApiError | null
  isLoading: boolean
  onClose: () => void
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      {error ? (
        <span role="alert" className="font-sans text-[11.5px]" style={{ color: 'var(--lc-red-ink)' }}>
          Works order {woKey} — {(error.body?.detail as string | undefined) ?? error.message}.
        </span>
      ) : (
        <span role="status" className="font-sans text-[11.5px]" style={{ color: 'var(--lc-mut)' }}>
          {isLoading ? `Loading the route for ${woKey}…` : `No route served for ${woKey}.`}
        </span>
      )}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close the works order"
        className="rounded-md border px-2.5 py-1 font-sans text-[11px] font-semibold"
        style={{ borderColor: 'var(--lc-b12)', background: 'var(--lc-card)', color: 'var(--lc-ink3)' }}
      >
        Close ✕
      </button>
    </div>
  )
}

export default function WorksOrderPanel({ entity, woKey, group, now, weeks, onClose }: {
  entity: string
  woKey: string
  /** The resource group the reader came from — its legs are accented in the route. */
  group: string
  now: string
  /** The SERVED window, so the backend labels which legs fall outside the grid the reader is
   *  looking at. It never clips the route — see services/works_order.py. */
  weeks: number
  onClose: () => void
}): React.JSX.Element {
  const { data, error, isLoading } = useWorksOrder(entity, woKey, weeks)
  return (
    <div className="overflow-hidden rounded-[10px] border" style={{ borderColor: 'var(--lc-b12)', background: 'var(--lc-panel)' }}>
      {!data && <Pending woKey={woKey} error={error} isLoading={isLoading} onClose={onClose} />}
      {data && (
        <>
          <Head route={data} onClose={onClose} />
          <WorksOrderRoute ops={data.ops} group={group} from={data.first_start} to={data.last_finish} now={now} />
          <Footnotes route={data} />
        </>
      )}
    </div>
  )
}
